import { watch } from 'vue';
import { useNotificationStore } from '../stores/notifications';
import { useAuthStore } from '../stores/auth';
import { useCompanyStore } from '../stores/company';
import { collection, onSnapshot, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider';
import { eventBus } from '../utils/eventBus';
import type { UserData } from '../domain/types/User';

export function useShipmentUpdates() {
  const notificationStore = useNotificationStore();
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();
  let unsubscribe: (() => void) | null = null;
  let isListenerActive = false; // ✅ Evitar inicializaciones duplicadas

  const cleanup = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      isListenerActive = false;
    }
  };

  const setupListener = () => {
    // ✅ Si ya hay un listener activo, no crear otro
    if (isListenerActive) {
      return;
    }
    
    cleanup();
    
    const db = FirebaseProvider.getInstance().getFirestore();
    const user = authStore.user as UserData;

    // ✅ Validar que el usuario exista
    if (!user) {
      console.warn('⚠️ Usuario no autenticado, listener desactivado');
      return;
    }

    // ✅ OPTIMIZACIÓN CRÍTICA: Solo escuchar shipments de la compañía seleccionada
    const activeCompanyId = companyStore.selectedCompanyId;
    
    if (!activeCompanyId) {
      console.warn('⚠️ No hay compañía seleccionada, listener desactivado');
      return;
    }

    // Verificar que el usuario tenga acceso a esta compañía
    const userCompanyIds = user.companiesRef?.map(c => c.id) || [];
    if (userCompanyIds.length > 0 && !userCompanyIds.includes(activeCompanyId)) {
      console.warn('⚠️ Usuario no tiene acceso a la compañía seleccionada');
      return;
    }

    const shipmentsRef = collection(db, 'Companies', activeCompanyId, 'shipments');
    const shipmentsQuery = query(shipmentsRef);

    isListenerActive = true; // ✅ Marcar como activo

    unsubscribe = onSnapshot(shipmentsQuery, async (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'modified') {
          const shipmentData = change.doc.data();
          const companyId = activeCompanyId;
          const hasAccess = true; // Ya filtramos por companyId arriba
          
          if (hasAccess) {
            const currentUser = authStore.user as UserData;
            
            // updatedBy es un objeto con { id, name, lastName }
            const updatedBy = shipmentData.updatedBy as { id: string; name: string; lastName: string } | undefined;
            const updatedById = updatedBy?.id || 'SYSTEM';
            const updatedByName = updatedBy?.name || 'Tracking';
            const updatedByLastName = updatedBy?.lastName || 'Integration';
            
            // Solo notificar si ES el usuario actual (cada uno ve solo sus propios cambios)
            // No notificar cambios de SYSTEM (tracking) ni de otros usuarios
            if (updatedById !== 'SYSTEM' && updatedById === currentUser.uid) {
              const changesCollectionRef = collection(change.doc.ref, 'changesHistory');
              const changesQuery = query(changesCollectionRef, orderBy('timestamp', 'desc'), limit(1));
              const changesSnapshot = await getDocs(changesQuery);
              
              const lastChange = changesSnapshot.docs[0]?.data();
              
              // Emitir el evento con la descripción del cambio
              eventBus.emit('shipment-updated', {
                userId: updatedById,
                userName: `${updatedByName} ${updatedByLastName}`,
                shipmentNumber: shipmentData.shipmentNumber || shipmentData.idNumber,
                shipmentId: change.doc.id,
                changes: lastChange?.changes || [],
                description: lastChange?.description // Incluir la descripción del cambio
              });

              notificationStore.addNotification({
                id: Math.random().toString(36).substr(2, 9),
                message: `${updatedByName} ${updatedByLastName} ha actualizado el embarque ${shipmentData.shipmentNumber || shipmentData.idNumber}`,
                timestamp: new Date(),
                shipmentId: change.doc.id,
                userId: updatedById,
                userName: `${updatedByName} ${updatedByLastName}`,
                companyId: companyId || ''
              });
            }
          }
        }
      }
    }, error => {
      console.error('[useShipmentUpdates] Error in shipment updates listener:', error);
    });
  };

  // ✅ Observar cambios en el usuario autenticado
  watch(() => authStore.user, (newUser) => {
    if (newUser) {
      setupListener();
    } else {
      cleanup();
    }
  }, { immediate: true });

  // ✅ Observar cambios en la compañía seleccionada para reactivar el listener
  watch(() => companyStore.selectedCompanyId, (newCompanyId) => {
    if (newCompanyId && authStore.user) {
      isListenerActive = false; // Resetear bandera para permitir nueva inicialización
      setupListener();
    }
  });

  return {
    setupListener,
    cleanup: cleanup // Aseguramos que cleanup se exporte como una función
  };
}